import * as immutable from 'object-path-immutable';
import { defaultDataIdFromObject, useMutation } from '@apollo/client';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router';
import TextareaAutosize from 'react-textarea-autosize';
import { translate } from 'translations/i18n';
import { useSelector, useDispatch } from 'react-redux';

import CCEvents, {
  EventNames,
  EventPropertyKeys,
} from '@companycam/companycam-events';
import { Badge, ButtonCondensed } from '@companycam/slab-web';

import GET_TASK_LIST_TEMPLATE_HEADER_INFO from '../gql/getTaskListTemplateHeaderInfo.graphql';
import { Modal } from 'components/core/modal/constants';
import { shouldUpdateValue } from '../helpers';
import TASK_TEMPLATE_DELETE from '../gql/taskTemplateDelete.graphql';
import {
  TaskListSectionsTemplateFragment,
  TaskListSectionlessTemplateFragment,
} from 'components/projects/taskList/gql/fragments';
import TASK_TEMPLATE_UPDATE from '../gql/taskTemplateUpdate.graphql';
import toast from 'helpers/toast';
import { setEditingTaskTemplateId } from 'actions/todos/templates';
import { setEditingTaskId } from 'actions/todos/taskLists';
import { useModal } from 'components/shared/hooks';
import {
  useTaskStatus,
  useTaskSave,
  useTaskDelete,
} from 'components/projects/taskList/hooks';
import useTaskTogglePhotoRequired from 'components/projects/taskList/hooks/useTaskTogglePhotoRequired';

import CompleteTaskCircle from './CompleteTaskCircle';
import CompletedByInfo from './CompletedByInfo';
import OutsideClickObserver from 'components/shared/OutsideClickObserver';
import SubTasks from 'components/projects/taskList/subTasks/Index';
import TaskAssets from 'components/projects/taskList/components/TaskAssets';
import TaskAssetsModal from 'components/projects/taskList/TaskAssetsModal';
import TaskMenuOptions from 'components/projects/taskList/components/TaskMenuOptions';
import ReferenceAssetGrid from 'components/projects/taskList/components/ReferenceAssetGrid';
import { fireStevenEntityCrudEventForTemplate } from '../templates/templateUpdateStevenEvent';

// #region styled-components
const TaskDetailsContainer = styled.div`
  display: flex;
  z-index: 1;
  flex: 1;
  width: 100%;
  padding-right: var(--cc_size_spacing_l);
`;

const TaskInputsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  flex-wrap: wrap;
  width: 100%;
  margin-left: var(--cc_size_spacing_s);
  align-items: flex-start;
`;

const TaskContainer = styled.div`
  position: relative;

  display: block;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;

  width: 100%;

  padding: var(--cc_size_spacing_l) var(--cc_size_spacing_m);
  margin-bottom: -1px;

  // Give task container a 1px solid border
  border: ${(props) => (!props.isPreviewingTemplate ? '1px solid' : '0')};

  // Default border color is transparent, override to blue in edit mode
  border-color: ${(props) =>
    props.isEditing ? 'var(--cc_color_link_primary)' : 'transparent'};

  // Change the bottom border only to gray to act as a divider between tasks, override to blue in edit mode
  // Also using a different variable for the border to avoid border colors multiplying together due to them having an alpha opacity value.
  border-bottom-color: ${(props) =>
    props.isEditing
      ? 'var(--cc_color_link_primary)'
      : 'var(--cc_color_background_2_darken_10)'};

  &:hover {
    // Change border color to gray on hover, override to blue in edit mode
    border-color: ${(props) =>
      props.completed
        ? 'var(--cc_color_utility_success)'
        : 'var(--cc_color_background_2_darken_10)'};

    border-color: ${(props) =>
      props.isEditing && 'var(--cc_color_border_input_active)'};

    #task-options-container {
      opacity: 1;
    }
  }

  @media screen and (min-width: 821px) {
    flex-direction: row;
  }
`;

const TaskTitleInputContainer = styled.div`
  textarea {
    &::placeholder {
      color: ${(props) => !props.isEditing && 'var(--cc_color_text_default)'};
    }
  }
  width: 100%;
`;

// parent task
const TaskTitleInput = styled(TextareaAutosize)`
  width: 100%;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  font-size: calc(var(--cc_size_text_m) + 1px);
  font-weight: 700;
  line-height: var(--cc_size_text_xl);
  text-decoration-thickness: var(--cc_size_border_width_s);
  resize: none;
  font-family: 'Averta', system-ui, sans-serif;
  cursor: ${({ readOnly }) => (readOnly ? 'default' : 'text')};
`;

const TaskInstructionsInputContainer = styled.div`
  textarea {
    display: ${({ emptyInstructions, readOnly }) =>
      emptyInstructions && readOnly ? 'none' : 'block'};
  }
  width: 100%;
`;

const TaskInstructionsInput = styled(TextareaAutosize)`
  width: 100%;
  padding: 0;
  margin: var(--cc_size_spacing_xs) 0 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  color: var(--cc_color_text_default);
  font-size: var(--cc_size_text_s);
  line-height: var(--cc_size_text_l);
  opacity: 1;
  resize: none;
  overflow-y: hidden;
  cursor: ${({ readOnly }) => (readOnly ? 'default' : 'text')};
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const TaskOutsideClickObserver = styled(OutsideClickObserver)`
  width: 100%;
`;

const ReferenceImagesContainer = styled.div`
  margin: 0 0 var(--cc_size_spacing_l) var(--cc_size_spacing_l);
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_m) 0;
  border-radius: var(--cc_size_border_radius_l);
`;

const ReferenceImageHeading = styled.div`
  font-weight: 600;
  font-size: var(--cc_size_text_s);
`;

const StyledBadge = styled(Badge)`
  margin: var(--cc_size_spacing_s) 0 var(--cc_size_spacing_s) 0;
`;
// #endregion

const messages = {
  addNotes: translate('projects.taskList.taskInputs.addNotes'),
  addReferencePhoto: translate('projects.taskList.addReferencePhoto'),
  deleteTaskTitle: translate(
    'projects.taskList.taskDeleteModal.deleteTaskTitle',
  ),
  deleteTaskBody: translate('projects.taskList.taskDeleteModal.deleteTaskBody'),
  untitledTask: translate('projects.taskList.taskInputs.untitledTask'),
  viewReferenceImages: translate('projects.taskList.viewReferenceImages'),
  referencePhotoHeading: translate(
    'projects.taskList.templateBodyContent.referencePhotoHeading',
  ),
};

const Task = ({
  listId,
  refetchHeaderInfo,
  sectionId,
  task,
  templateProjectId,
  viewOnly,
  templateEditMode,
  isTemplate,
  isPreviewingTemplate,
  hideCompletedTasks,
  isChecklistAdmin,
}) => {
  const { id } = useParams();
  const projectId = id || templateProjectId;
  const [isTaskAssetOpen, setIsTaskAssetOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState(task.title);
  const [taskInstructions, setTaskInstructions] = useState(task.instructions);
  const { toggleTaskStatus } = useTaskStatus({
    taskId: task.id,
    taskListId: listId,
    skipOptimisticUpdate: hideCompletedTasks,
  });
  const { deleteTask } = useTaskDelete(listId);
  const dispatch = useDispatch();
  const editingTaskId = useSelector(
    ({ todos }) => todos.taskLists.editingTaskId,
  );
  const editingTaskTemplateId = useSelector(
    ({ todos }) => todos.templates.editingTaskId,
  );
  const isEditing = isTemplate
    ? editingTaskTemplateId === task.id
    : editingTaskId === task.id;

  const subtasks = get(
    task,
    isTemplate ? 'subTaskTemplates.nodes' : 'subTasks.nodes',
    [],
  );
  const shouldRenderSubtasks = isEditing || subtasks.length > 0;
  const taskImages = task?.images?.nodes || [];
  const { taskSave } = useTaskSave({
    taskListId: listId,
    taskListSectionId: sectionId,
  });
  const inputContainerRef = useRef(null);
  const titleInputRef = useRef(null);

  const { togglePhotoRequired } = useTaskTogglePhotoRequired({
    taskId: task.id,
  });

  const [updateTaskTemplate] = useMutation(TASK_TEMPLATE_UPDATE, {
    onCompleted: () =>
      fireStevenEntityCrudEventForTemplate({
        templateNodeId: listId,
        entityType: 'checklist_template_field',
        action: 'updated',
      }),
    onError: () =>
      toast.error(translate('projects.taskList.task.taskUpdateError')),
    refetchQueries: [
      {
        query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
        variables: { id: listId },
      },
      'GetTaskListTemplateHeaderInfo',
    ],
  });

  const taskTemplateUpdateInput = {
    id: task.id,
    photoCaptureRequired: task.photoCaptureRequired,
    title: taskTitle,
    instructions: taskInstructions,
  };

  const taskTemplateUpdateOptimisticResponse = ({ input }) => {
    return {
      taskTemplateUpdate: {
        errors: [],
        taskTemplate: {
          ...task,
          ...input,
        },
        __typename: 'TaskTemplateUpdatePayload',
      },
      __typename: 'Mutation',
    };
  };

  const toggleTaskTemplatePhotosRequired = () => {
    updateTaskTemplate({
      variables: {
        input: {
          ...taskTemplateUpdateInput,
          photoCaptureRequired: !task.photoCaptureRequired,
        },
      },
      optimisticResponse: taskTemplateUpdateOptimisticResponse,
    });
  };

  const onTogglePhotoRequiredClick = async () => {
    if (isTemplate) {
      toggleTaskTemplatePhotosRequired();
      return;
    }
    if (isChecklistAdmin) {
      if (task.completed && !taskImages.length && !task.photoCaptureRequired) {
        await toggleTaskStatus(task);
      }
      togglePhotoRequired({
        optimisticResponse: {
          taskTogglePhotoRequired: {
            errors: [],
            task: {
              ...task,
              photoCaptureRequired: !task.photoCaptureRequired,
            },
            __typename: 'TaskTogglePhotoRequiredPayload',
          },
          __typename: 'Mutation',
        },
      });
    }
  };

  const { showModal, hideModal } = useModal();

  const handleOnBlur = (key, value) => {
    if (!shouldUpdateValue(task[key], value)) {
      return;
    }

    if (isTemplate) {
      updateTaskTemplate({
        variables: {
          input: taskTemplateUpdateInput,
        },
        optimisticResponse: taskTemplateUpdateOptimisticResponse,
      });
    } else {
      taskSave({ ...task, title: taskTitle, instructions: taskInstructions });
    }
  };

  const confirmDeleteTask = async () => {
    await deleteTask(task);
    hideModal(Modal.CONFIRMATION);
  };

  const [deleteTaskTemplate] = useMutation(TASK_TEMPLATE_DELETE, {
    onCompleted: () => {
      hideModal(Modal.CONFIRMATION);
      fireStevenEntityCrudEventForTemplate({
        templateNodeId: listId,
        entityType: 'checklist_template_field',
        action: 'deleted',
      });
    },
    onError: () =>
      toast.error(translate('projects.taskList.taskDeleteModal.deleteError')),
    refetchQueries: [
      {
        query: GET_TASK_LIST_TEMPLATE_HEADER_INFO,
        variables: { id: listId },
      },
      'GetTaskListTemplateHeaderInfo',
    ],
    update: (
      cache,
      {
        data: {
          taskTemplateDelete: { deletedId },
        },
      },
    ) => {
      const cacheId = defaultDataIdFromObject({
        id: listId,
        __typename: 'TaskListTemplate',
      });

      if (sectionId) {
        const cachedData = cache.readFragment({
          id: cacheId,
          fragment: TaskListSectionsTemplateFragment,
          fragmentName: 'TaskListSectionsTemplateFragment',
        });

        const sectionTemplates = get(
          cachedData,
          'taskListSectionTemplates.nodes',
          [],
        );
        const sectionIndex = sectionTemplates.findIndex(
          (section) => section.id === sectionId,
        );
        const taskTemplates = get(
          sectionTemplates,
          `${sectionIndex}.taskTemplates.nodes`,
          [],
        );
        const updatedData = immutable.set(
          cachedData,
          `taskListSectionTemplates.nodes.${sectionIndex}.taskTemplates.nodes`,
          taskTemplates.filter((taskTemplate) => taskTemplate.id !== deletedId),
        );

        cache.writeFragment({
          fragment: TaskListSectionsTemplateFragment,
          fragmentName: 'TaskListSectionsTemplateFragment',
          id: cacheId,
          data: updatedData,
        });
      } else {
        const cachedData = cache.readFragment({
          id: cacheId,
          fragment: TaskListSectionlessTemplateFragment,
          fragmentName: 'TaskListSectionlessTemplateFragment',
        });
        const taskTemplates = get(cachedData, 'taskTemplates.nodes', []);

        const updatedData = immutable.set(
          cachedData,
          'taskTemplates.nodes',
          taskTemplates.filter((taskTemplate) => taskTemplate.id !== deletedId),
        );

        cache.writeFragment({
          fragment: TaskListSectionlessTemplateFragment,
          fragmentName: 'TaskListSectionlessTemplateFragment',
          id: cacheId,
          data: updatedData,
        });
      }
    },
    variables: {
      input: {
        id: task.id,
      },
    },
  });

  const handleDeleteTaskTemplate = () => {
    showModal(Modal.CONFIRMATION, {
      confirmAction: deleteTaskTemplate,
      confirmLabel: translate('common.confirmDelete'),
      cancelLabel: translate('common.cancelDelete'),
      message: messages.deleteTaskBody,
      title: messages.deleteTaskTitle,
      onRequestClose: hideModal(Modal.CONFIRMATION),
    });
  };

  const handleOnClickDelete = () => {
    if (isTemplate) {
      handleDeleteTaskTemplate();
    } else {
      showModal(Modal.CONFIRMATION, {
        confirmAction: confirmDeleteTask,
        confirmLabel: translate('common.confirmDelete'),
        cancelLabel: translate('common.cancelDelete'),
        message: messages.deleteTaskBody,
        title: messages.deleteTaskTitle,
        onRequestClose: hideModal(Modal.CONFIRMATION),
      });
    }
  };

  const handleClickOutside = () => {
    if (isTemplate) {
      dispatch(setEditingTaskTemplateId(null));
    } else {
      dispatch(setEditingTaskId(null));
    }
  };

  const handleEditClick = (taskId) => {
    if (isTemplate) {
      dispatch(setEditingTaskTemplateId(taskId));
    } else {
      dispatch(setEditingTaskId(taskId));
    }
  };

  const handleReferenceClick = () => {
    showModal(Modal.REFERENCE_PHOTOS_MODAL, {
      listId,
      isTemplate,
      onRequestClose: hideModal,
      projectId,
      refetchHeaderInfo,
      taskId: task.id,
    });
    CCEvents.logEvent(EventNames.TASK_LISTS.VIEW_REFERENCE_IMAGES, {
      [EventPropertyKeys.PROJECT_ID]: projectId,
      [EventPropertyKeys.TASK_LIST_ID]: listId,
    });
  };

  useEffect(() => {
    // Issue with react-router and turbolinks and library react-textarea-autosize
    // hiddenTextarea element is still referenced when navigating using links, the
    // browser creates a new document.body but hiddenTextarea keeps pointing at old body.
    // deleting hidden textarea when navigating away forces library to resize textarea elements
    // see github issue https://github.com/Andarist/react-textarea-autosize/issues/247
    // can remove once PR https://github.com/Andarist/react-textarea-autosize/pull/292
    return () => {
      const textAreaResizeElementTrigger = document.querySelector(
        'textarea[tabindex="-1"][aria-hidden="true"]',
      );
      if (textAreaResizeElementTrigger) {
        textAreaResizeElementTrigger.remove();
      }
    };
  }, []);

  useEffect(() => {
    const { current } = titleInputRef;
    if (isEditing && current) {
      const length = current.value.length;
      current.focus({ preventScroll: true });
      current.setSelectionRange(length, length);
    }
  }, [inputContainerRef, isEditing]);

  const renderTask = () => (
    <TaskContainer
      className="task-container"
      isTemplate={isTemplate}
      isEditing={isEditing}
      completed={task.completed}
      isPreviewingTemplate={isPreviewingTemplate}
    >
      <TaskDetailsContainer>
        <CompleteTaskCircle
          disabled={isTemplate}
          task={task}
          taskListId={listId}
          hideCompletedTasks={hideCompletedTasks}
        />
        <TaskInputsContainer>
          <InputContainer ref={inputContainerRef}>
            <TaskTitleInputContainer
              isEditing={isEditing}
              completed={task.completed || false}
            >
              <TaskTitleInput
                onBlur={() => handleOnBlur('title', taskTitle)}
                onChange={(e) => setTaskTitle(e.target.value)}
                placeholder={messages.untitledTask}
                readOnly={!isEditing}
                value={taskTitle}
                ref={titleInputRef}
              />
            </TaskTitleInputContainer>

            <CompletedByInfo
              completed={task.completed}
              completedByUserName={task.completedBy?.name}
              completedAt={task.completedAt}
              completedByCompany={task.completedBy?.company}
            />

            <div style={{ display: 'flex' }} />
            {task.instructions.length || isEditing ? (
              <TaskInstructionsInputContainer
                completed={task.completed}
                emptyInstructions={!taskInstructions}
              >
                <TaskInstructionsInput
                  onBlur={() => handleOnBlur('instructions', taskInstructions)}
                  onChange={(e) => setTaskInstructions(e.target.value)}
                  placeholder={messages.addNotes}
                  readOnly={!isEditing}
                  value={taskInstructions}
                />
              </TaskInstructionsInputContainer>
            ) : null}
          </InputContainer>

          {task.photoCaptureRequired ? (
            <StyledBadge iconName="information-outline" color="info">
              {translate('projects.taskList.task.taskPhotoRequired')}
            </StyledBadge>
          ) : null}

          {task.referencePhotos.length && !isPreviewingTemplate ? (
            <ButtonCondensed
              color="primary"
              size="small"
              aria-label={messages.viewReferenceImages}
              style={{ marginTop: 12 }}
              disabled={viewOnly}
              onClick={handleReferenceClick}
              icon={{
                name: 'compare',
                position: 'before',
              }}
            >
              {messages.viewReferenceImages}
            </ButtonCondensed>
          ) : null}
        </TaskInputsContainer>
      </TaskDetailsContainer>
      <TaskMenuOptions
        isVisible={!isPreviewingTemplate}
        isEditState={isEditing}
        photoCaptureRequired={task.photoCaptureRequired}
        showEditButton={isChecklistAdmin || isTemplate}
        showDeleteButton={isChecklistAdmin || isTemplate}
        showAddTaskPhotoButton={isTemplate}
        onTogglePhotoRequiredClick={onTogglePhotoRequiredClick}
        onDeleteClick={handleOnClickDelete}
        onEditClick={() => handleEditClick(task.id)}
        onAddPhotosClick={() => setIsTaskAssetOpen(true)}
        onAddReferencePhotoClick={handleReferenceClick}
      />
      <TaskAssets task={task} toggleTaskStatus={toggleTaskStatus} />
      {isPreviewingTemplate && task.referencePhotos.length > 0 ? (
        <ReferenceImagesContainer>
          <ReferenceImageHeading>
            {messages.referencePhotoHeading}
          </ReferenceImageHeading>
          <ReferenceAssetGrid assets={task.referencePhotos} viewOnly />
        </ReferenceImagesContainer>
      ) : null}
      {shouldRenderSubtasks ? (
        <SubTasks
          isEditingTask={isEditing}
          isTemplate={isTemplate}
          listId={listId}
          task={task}
          subtasks={subtasks}
          templateEditMode={templateEditMode}
          hideCompletedTasks={hideCompletedTasks}
        />
      ) : null}
      <TaskAssetsModal
        isOpen={isTaskAssetOpen}
        onRequestClose={() => setIsTaskAssetOpen(false)}
        taskId={task.id}
      />
    </TaskContainer>
  );

  return isEditing ? (
    <TaskOutsideClickObserver onClickOutside={handleClickOutside}>
      {renderTask()}
    </TaskOutsideClickObserver>
  ) : (
    renderTask()
  );
};

Task.defaultProps = {
  refetchHeaderInfo: () => {},
  sectionId: null,
  viewOnly: false,
};

Task.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    details: PropTypes.string.isRequired,
    instructions: PropTypes.string.isRequired,
    completed: PropTypes.bool,
    completedBy: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      company: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        accentColor: PropTypes.string.isRequired,
      }).isRequired,
    }),
    completedAt: PropTypes.string,
    photoCaptureRequired: PropTypes.bool.isRequired,
    referencePhotos: PropTypes.arrayOf(PropTypes.object).isRequired,
    images: PropTypes.shape({
      nodes: PropTypes.object,
    }),
  }).isRequired,
  refetchHeaderInfo: PropTypes.func,
  sectionId: PropTypes.string,
  listId: PropTypes.string.isRequired,
  viewOnly: PropTypes.bool,
  templateProjectId: PropTypes.string,
  templateEditMode: PropTypes.bool,
  isTemplate: PropTypes.bool,
  isPreviewingTemplate: PropTypes.bool,
  hideCompletedTasks: PropTypes.bool,
  isChecklistAdmin: PropTypes.bool,
};

export default Task;
